import { useEffect, useState } from 'react'
import { getduplicateLoans } from 'services'

import { DuplicateLoansTable, IDuplicateLoansData } from './DuplicateLoansTable'

interface IProps {
  /** Loan whose related loans to list. Nothing renders until this is a real loan number. */
  loanNumber: string | number | undefined
}

/**
 * The Related Loans box: every other loan sharing this file's borrower, entity or subject property.
 *
 * Owns its own fetch so a screen can drop it in with the loan number alone - it is on both the
 * origination Loan Overview and the servicing screen, and those two had no reason to keep separate
 * copies of the same loading glue.
 *
 * Paid-off related loans are left out of the first fetch - a repeat borrower's closed files are
 * not what anyone opens this box for - and the table's own toggle fetches them back in.
 *
 * Renders nothing at all when there are no related loans, when the loan is unsaved, or when the
 * user lacks the Potential Duplicate permission (the endpoint answers `success: false`), so a
 * caller never has to guard it.
 */
export const RelatedLoansPanel = ({ loanNumber }: IProps) => {
  const [relatedLoans, setRelatedLoans] = useState<IDuplicateLoansData | null>(null)
  const [includePaidOff, setIncludePaidOff] = useState(false)

  useEffect(() => {
    const no = String(loanNumber ?? '').trim()
    // A new structure stores the loan number as "New" (the route itself is lowercase "new").
    // Neither is a saved loan, so there is nothing to look up.
    if (!no || no.toLowerCase() === 'new' || no === '-' || no === 'undefined') {
      setRelatedLoans(null)
      return
    }

    let stale = false
    // A different loan starts hidden again, whatever was being shown on the last one.
    setIncludePaidOff(false)
    getduplicateLoans(no, { skip: 0, count: 2, includePaidOff: false })
      .then((res: any) => {
        // A loan switched underneath us (the servicing screen keeps its shell across loans) must not
        // be shown another loan's related files.
        if (stale) return
        const paidOffCount = res?.paidOffCount ?? 0
        // Kept on screen when every related loan is paid off: there is no table then, but the box
        // still has to offer the toggle rather than pretend the loans do not exist.
        const hasSomethingToShow = (Array.isArray(res?.data) && res.data.length > 0) || paidOffCount > 0
        if (res?.success && hasSomethingToShow) {
          setRelatedLoans({
            headers: res.headers ?? [],
            pipelineHeaders: res.pipelineHeaders ?? [],
            data: res.data ?? [],
            additionalData: res.additionalData ?? [],
            total: res.total ?? res.data?.length ?? 0,
            potentialDuplicateCount: res.potentialDuplicateCount ?? 0,
            paidOffCount,
            totalLoanAmount: res.totalLoanAmount ?? 0,
          })
        } else {
          setRelatedLoans(null)
        }
      })
      .catch(() => {
        if (!stale) setRelatedLoans(null)
      })

    return () => {
      stale = true
    }
  }, [loanNumber])

  if (!relatedLoans || (!relatedLoans.data.length && !relatedLoans.paidOffCount)) return null

  return (
    <DuplicateLoansTable
      loanNumber={String(loanNumber)}
      duplicateLoans={relatedLoans}
      onDuplicateLoansChange={setRelatedLoans}
      includePaidOff={includePaidOff}
      onIncludePaidOffChange={setIncludePaidOff}
    />
  )
}
